import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import Sheet from './Sheet';

const ConfirmContext = createContext(null);

/**
 * Asking before something that can't be undone, in the app's own pop-up
 * instead of the browser's box.
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Delete Thursday squad?', message: '...' }))) return;
 *
 * title: the question. message: one line on what will happen.
 * confirmLabel: defaults to "Delete". cancelLabel: defaults to "Cancel".
 * tone: 'danger' (solid red, the default) or 'primary' (money, sending).
 * input: { label, placeholder, required, multiline } asks for some text too;
 *   the promise then gives the text (or false).
 * Resolves true (or the text) to go ahead, false to stop.
 */
export function ConfirmProvider({ children }) {
  const [ask, setAsk] = useState(null);
  const [text, setText] = useState('');
  const resolver = useRef(null);

  const confirm = useCallback((options) => new Promise((resolve) => {
    // A second question replaces the first, which counts as "no"
    if (resolver.current) resolver.current(false);
    resolver.current = resolve;
    setText('');
    setAsk(options);
  }), []);

  const finish = (answer) => {
    const resolve = resolver.current;
    resolver.current = null;
    setAsk(null);
    if (resolve) resolve(answer);
  };

  const input = ask?.input;
  const blocked = input?.required && !text.trim();
  const go = () => { if (!blocked) finish(input ? text.trim() : true); };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {ask && (
        <Sheet
          title={ask.title}
          onClose={() => finish(false)}
          onSubmit={input && !input.multiline ? go : undefined}
          initialFocus={input ? 'input, textarea' : '[data-confirm-cancel]'}
          footer={(
            <div className="bk-actions">
              <button
                type={input && !input.multiline ? 'submit' : 'button'}
                className={`bk-btn ${ask.tone === 'primary' ? 'bk-btn--primary' : 'bk-btn--danger'}`}
                disabled={blocked}
                onClick={input && !input.multiline ? undefined : go}
              >
                {ask.confirmLabel || 'Delete'}
              </button>
              <button type="button" className="bk-btn" data-confirm-cancel onClick={() => finish(false)}>
                {ask.cancelLabel || 'Cancel'}
              </button>
            </div>
          )}
        >
          {ask.message && <p className="ui-sheet__message">{ask.message}</p>}
          {input && (
            <label className="bk-label">
              {input.label}
              {!input.required && <span className="bk-optional"> (optional)</span>}
              {input.multiline ? (
                <textarea className="bk-input" value={text} placeholder={input.placeholder} onChange={(e) => setText(e.target.value)} />
              ) : (
                <input className="bk-input" value={text} placeholder={input.placeholder} onChange={(e) => setText(e.target.value)} enterKeyHint="done" />
              )}
            </label>
          )}
        </Sheet>
      )}
    </ConfirmContext.Provider>
  );
}

// Outside the provider (some tests) it falls back to the browser's box
export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  return confirm || fallbackConfirm;
}

function fallbackConfirm(options) {
  if (options?.input) {
    const answer = window.prompt([options.title, options.message].filter(Boolean).join('\n'));
    return Promise.resolve(answer === null ? false : answer.trim());
  }
  return Promise.resolve(window.confirm([options.title, options.message].filter(Boolean).join('\n')));
}
